<!-- https://github.com/David-Desmaisons/vue-plotly -->

<template>
  <div class="w-100">
    <Plotly
      v-if="typeof(getDataCPConf)!=='undefined' && getDataCPConf.length > 0"
      ref="plot"
      :data="getDataCPConf"
      :layout="getLayoutCPConf"
      :display-mode-bar="true"
      :displaylogo="false"
      :editable="true"
      @mouseover="hoverPlotly = true"
      @mouseleave="hoverPlotly = false"
      @relayout="plotReshaped"
      @click="clickGraph"
    />
    <div
      v-else
      class="d-flex justify-content-center h-100"
    >
      <b-spinner
        variant="primary"
        label="Loading"
      />
    </div>
    <b-container
      v-if="isSelectedCPConf"
      id="selectedInfo"
      class="mt-2 conc-container"
    >
      <b-row
        class="mt-2"
        align-v="center"
      >
        <b-col md="auto">
          <label
            for="selecXValue"
            class="num-lbl-inpt mb-0"
          >Time (s): </label>
        </b-col>
        <b-col
          cols="3"
        >
          <b-form-input
            id="selecXValue"
            v-model="selectedXValue"
            type="number"
            class="num-inpt"
            size="sm"
          />
        </b-col>
        <b-col md="auto">
          <label
            for="selecConc"
            class="num-lbl-inpt mb-0"
          >{{ getNotationReportViewer }} ({{ getUnitsReportViewer }}): </label>
        </b-col>
        <b-col
          cols="3"
        >
          <b-form-input
            id="selecConc"
            v-model="selectedConc"
            type="number"
            class="num-inpt"
            size="sm"
          />
        </b-col>
      </b-row>
      <b-row align-h="end">
        <b-button
          variant="outline-primary"
          class="mx-2 mt-0 mb-2"
          size="sm"
          @click="clickOK"
        >
          OK
        </b-button>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { Plotly } from 'vue-plotly'
import { mapGetters, mapActions } from 'vuex'
import defaultRectShape from '@/assets/configurationWindow/defaultRectShape.JSON'

/// Constants
const reShapeChange = /shapes\[(\d+)\]\.[yx][01]/g
// Limits to delete
const minDeleteY0 = -0.3
const maxDeleteY0 = 0.35
// Colors
const rectFillColor = '#d3d3d3' // It should be moved to Vuex, with a value of the original plot
const rectSelectedColor = '#66b2ff' // Color when the rectangle is selected
// Proportinal Size Main Container
const proportionalHeightOpened=0.65
const proportionalHeightClosed=0.87
const proportionalWidth=0.8

export default {
  name: 'InteractivePlotCP',
  components: {
    Plotly
  },
  props:{
    showLegends:{
      type:Boolean,
      required: false,
      default:false
    },
  },
  data(){
    return{
      hoverPlotly:false,
    }
  },
  computed: {
    ...mapGetters([
      'getDataCPConf',
      'getLayoutCPConf',
      'getStepSizeCPConf',
      'getSelectedXAxisCPConf',
      'getSelectConcCPConf',
      'getSelectedIndxCPConf',
      'isSelectedCPConf',
      'getWidthConfWindow',
      'getHeigthConfWindow',
      'getConcentrationsCPConf',
      'getUnitsReportViewer',
      'getNotationReportViewer'
    ]),
    // Properties used by the elements of the template
    selectedXValue: {
      set (value) {
        this.setSelectedXAxisCPConf(value)
        this.$emit('change')
      },
      get () {
        return this.getSelectedXAxisCPConf
      }
    },
    selectedConc: {
      set (value) {
        this.setSelectedConcCPConf(parseFloat(value))
        this.$emit('change')
      },
      get () {
        return this.getSelectConcCPConf
      }
    }
  },
  watch: {
    showLegends(){
      this.updateLegendsPlotly()
    },
    getDataCPConf(){
      this.updateHeigth(this.getHeigthConfWindow)
      this.updateConcentrations()
    },
    getStepSizeCPConf (newValue) {
      // Update the size of the rectangles in the plot
      this.changeStepSizeGraph(newValue)
    },
    getSelectedIndxCPConf (newValue) {
      // When the index of the selected item changes, it changes the color to identify that element
      const extractedLayout = { shapes: this.getLayoutCPConf.shapes }
      for (let i = 0; i < extractedLayout.shapes.length; i++) { // Cycle to restore the original color
        extractedLayout.shapes[i].fillcolor = rectFillColor
      }
      if (newValue >= 0 && newValue < extractedLayout.shapes.length) { // Verify that the index is in the limits
        extractedLayout.shapes[newValue].fillcolor = rectSelectedColor // update the color to the selected
        this.setSelectionFlagCPConf(true)// Ensures that the selection flags is set
        const centerSelected = (extractedLayout.shapes[newValue].x1 + extractedLayout.shapes[newValue].x0) / 2
        this.setSelectedXAxisCPConf(centerSelected)// Updates the value of the selected X-axis imput
        this.$emit('change')
      }
      this.setShapesGraphCPConf(extractedLayout.shapes)
      this.$refs.plot.relayout(extractedLayout)// Updates the Plotly Layout
    },
    isSelectedCPConf (newValue) {
      if (!newValue) { // When it is nothing selected changes the indx to -1, it restores the color and prepares for the next change
        this.setSelectedIndxCPConf(-1)
      }
      this.updateHeigth(this.getHeigthConfWindow)
    },
    getSelectedXAxisCPConf (newValue) {
      // Updates the position of the selected rectangle
      if (this.isSelectedCPConf) { // When the value of the X-axis selected changes and it is something selected
        const extractedLayout = { shapes: this.getLayoutCPConf.shapes }
        extractedLayout.shapes[this.getSelectedIndxCPConf].x0 = newValue - (this.getStepSizeCPConf / 2)
        extractedLayout.shapes[this.getSelectedIndxCPConf].x1 = newValue + (this.getStepSizeCPConf / 2)
        this.setShapesGraphCPConf(extractedLayout.shapes)
        this.$refs.plot.relayout(extractedLayout)// Set the Plotly Layout
      }
    },
    getHeigthConfWindow(newValue){
      this.updateHeigth(newValue)
    },
    getWidthConfWindow(newValue){
      if(!('plot' in this.$refs) || typeof(this.$refs.plot)=='undefined'){
        return
      }
      const width= newValue*proportionalWidth
      const updatedLayout =
                {
                  width: width,
                  autosize: false
                }
      this.$refs.plot.relayout(updatedLayout)
      this.$refs.plot.relayout({ autosize: true })
    }
  },
  mounted() {
    this.changeStepSizeGraph(this.getStepSizeCPConf)
    this.updateHeigth(this.getHeigthConfWindow)
    this.updateConcentrations()
  },
  methods: {
    // It is trigegred when the plotly graph is reshaped, it is more broad that what we require, so we need to filter the event
    plotReshaped(event) {
      const keysStr = Object.keys(event).join(',') // Join all the events key in a single string to execute the regex
      const rgxShapeChangeMatch = [...keysStr.matchAll(reShapeChange)] // Get all the matches with the shape arguments.

      if (rgxShapeChangeMatch.length === 4) { // Validate if it is a change in a rectangle. It is exactly four matches of the Regex. Otherwise it could be an action not performed by the user
        const indxShape = parseInt(rgxShapeChangeMatch[0][1]) // Get the index of the modified shape
        const extractedLayout = { shapes: this.getLayoutCPConf.shapes } // Create an object with the Layout, it would be the only thing that would modify after

        // Delete condition. When Y0 is higher/lower than specific limit
        if (extractedLayout.shapes[indxShape].y0 < minDeleteY0 || extractedLayout.shapes[indxShape].y0 > maxDeleteY0) {
          extractedLayout.shapes.splice(indxShape, 1)// Remove from the layout
          this.deleteConcentrationCPConf(indxShape)// remove from the concentration in Vuex
          if (this.isSelectedCPConf && this.getSelectedIndxCPConf === indxShape) { // If it was the current selected item
            this.setSelectionFlagCPConf(false)
          }
        } else {
          const shapeStepSize = this.getLayoutCPConf.shapes[indxShape].x1 - this.getLayoutCPConf.shapes[indxShape].x0
          this.setStepSizeCPConf(shapeStepSize)

          // All the changes in the shape select the new value
          this.setSelectedIndxCPConf(indxShape)
          const centerSelected = (extractedLayout.shapes[indxShape].x1 + extractedLayout.shapes[indxShape].x0) / 2
          this.updateXAxisConcCPConf({index:indxShape,newXVal:centerSelected})

          // Update the X position if we move the selected rectangle
          if (this.isSelectedCPConf && this.getSelectedIndxCPConf === indxShape) {
            this.setSelectedXAxisCPConf(centerSelected)
          }

          // Verify that the y shape is correct
          extractedLayout.shapes[indxShape].y0 = 0
          extractedLayout.shapes[indxShape].y1 = 1
        }

        this.$emit('change')
        this.setShapesGraphCPConf(extractedLayout.shapes)
        this.$refs.plot.relayout(extractedLayout) // Update the Plotly graph
      }
    },
    clickGraph(event) { // Click on the plotly graph creates a new rectangle
      if (event.points.length > 0) { // It requires that there is some point of the graph to reference the X value
        const positionX = event.points[0].x // Use any X value, all are the same
        const extractedLayout = { shapes: this.getLayoutCPConf.shapes }
        const newShape = JSON.parse(JSON.stringify(defaultRectShape)); //Deep copy of the base rect
        // Set the positions of the new rectangle
        newShape.y0 = 0
        newShape.y1 = 1
        newShape.x0 = positionX - (this.getStepSizeCPConf / 2)
        newShape.x1 = positionX + (this.getStepSizeCPConf / 2)

        this.addConcentrationCPConf(positionX)// Add the new concentration to the Vuex State

        const newShapeIndx = extractedLayout.shapes.length
        extractedLayout.shapes.push(newShape) // Add the rectangle to the layout
        this.setSelectedIndxCPConf(newShapeIndx)// Set the new rectangle as the selected component

        this.setShapesGraphCPConf(extractedLayout.shapes)
        this.$refs.plot.relayout(extractedLayout) // Update the Plotly layout
        this.$emit('change')
      }
    },
    updateStepSize() { // It updates the Step Size input, it make it matches with the width of the rectangles
      if (this.getLayoutCPConf.shapes.length > 0 && 'x0' in this.getLayoutCPConf.shapes[0] &&
                    'x1' in this.getLayoutCPConf.shapes[0]) { // Check if it is possible to extract the value
        const newStepSize = this.getLayoutCPConf.shapes[0].x1 - this.getLayoutCPConf.shapes[0].x0
        this.setStepSizeCPConf(newStepSize)
      }
    },
    clickOK() { // Event triggered when the button OK is clicked
      this.setSelectionFlagCPConf(false) // When the button OK is clicked, there is no selected item.
    },
    updateHeigth(newValue){
      if(!('plot' in this.$refs) || typeof(this.$refs.plot)=='undefined'){
        return
      }

      let height = newValue;
      if(this.isSelectedCPConf){
        height*=proportionalHeightOpened
      }
      else{
        height*=proportionalHeightClosed
      }
      const updatedLayout =
                {
                  height: height,
                  autosize: false
                }
      this.$refs.plot.relayout(updatedLayout)
      this.$refs.plot.relayout({ autosize: true })
    },
    changeStepSizeGraph(newValue){
      if(typeof(this.getLayoutCPConf)==='undefined'||typeof(this.$refs.plot)==='undefined'){
        return
      }

      if(typeof(this.getLayoutCPConf.shapes)==='undefined'|| this.getLayoutCPConf.shapes.length===0){
        this.updateConcentrations()
      }

      const halfNewVal = (newValue) / 2
      const extractedLayout = { shapes: this.getLayoutCPConf.shapes }
      for (let i = 0; i < extractedLayout.shapes.length; i++) { // update all the rectangles in the Shapes of the layout
        if ('x0' in this.getLayoutCPConf.shapes[i] && 'x1' in this.getLayoutCPConf.shapes[i]) { // Verifies that are rectangles.
          const center = (extractedLayout.shapes[i].x1 + extractedLayout.shapes[i].x0) / 2
          extractedLayout.shapes[i].x1 = center + halfNewVal
          extractedLayout.shapes[i].x0 = center - halfNewVal
        }
      }
      this.setShapesGraphCPConf(extractedLayout.shapes)
      this.$refs.plot.relayout(extractedLayout)
    },
    updateConcentrations(){
      const extractedLayout = { shapes: [] }
      for(let j=0;j<this.getConcentrationsCPConf.length;j++){
        const newShape = JSON.parse(JSON.stringify(defaultRectShape)); //Deep copy of the base rect
        let positionX = this.getConcentrationsCPConf[j][0]
        // Set the positions of the new rectangle
        newShape.y0 = 0
        newShape.y1 = 1
        newShape.x0 = positionX - (this.getStepSizeCPConf / 2)
        newShape.x1 = positionX + (this.getStepSizeCPConf / 2)
        if(this.isSelectedCPConf && this.getSelectedIndxCPConf === j){
          newShape.fillcolor = rectSelectedColor
        }
        extractedLayout.shapes.push(newShape) // Add the rectangle to the layout
      }
      this.setShapesGraphCPConf(extractedLayout.shapes)
      this.$refs.plot.relayout(extractedLayout)
    },
    updateLegendsPlotly(){
      this.$refs.plot.relayout({showlegend:this.showLegends})
    },
    ...mapActions(['setLayoutPlot', 'setStepSizeCPConf', 'setSelectedXAxisCPConf', 'setSelectedConcCPConf', 'setSelectedIndxCPConf', 'setSelectionFlagCPConf', 'addConcentrationCPConf', 'deleteConcentrationCPConf','setXValueConcCPConf','updateXAxisConcCPConf','setShapesGraphCPConf'])
  }
}
</script>

<style scoped>
  .conc-container{
    border: black solid 1.5px;
    background-color: #EEEEEE;
    font-size: 0.9rem;
  }
</style>
